// Practice-level settings (one row per organisation, organisations.settings jsonb).
// Pure: defaults + a tolerant merge so a partial or stale stored value can never
// break the app. Validation of writes lives server-side (routes/settings.ts).
import { SEGMENTS, type Segment } from './types.js';
import { DEFAULT_ENTITY_RULES, type EntityRules } from './entities.js';
import { RULE_KEYS, type ComplianceRuleVersion } from './compliance.js';

export interface PracticeSettings {
  /** Months between calls per service tier; null = no scheduled calls. */
  callCadenceMonths: Record<Segment, number | null>;
  feeConsent: {
    /** Days after the consent due date the client still has to renew (0 = the due date is the deadline). */
    renewalWindowDays: number;
    /** Days before the due date to start warning. */
    warnDays: number;
  };
  /** Review windows for SMSFs, trusts and companies. */
  entities: EntityRules;
  /** Compliance check windows, as dated versions (empty = the built-in defaults). */
  compliance: { versions: ComplianceRuleVersion[] };
  digest: {
    /** Send the morning "Today" email. */
    enabled: boolean;
    /** Recipients; empty = the organisation's active admin + adviser users. */
    recipients: string[];
  };
}

/**
 * Defaults, chosen 2026-09-29 (Scott to confirm cadence): Diamond monthly,
 * Platinum every 2 months, Gold quarterly, Silver half-yearly, Bronze yearly,
 * Inactive none. Fee consent: 60-day renewal window after the due date, warn
 * 30 days ahead. Change these on the Today page's settings panel.
 */
export const DEFAULT_SETTINGS: PracticeSettings = {
  callCadenceMonths: { Diamond: 1, Platinum: 2, Gold: 3, Silver: 6, Bronze: 12, Inactive: null },
  feeConsent: { renewalWindowDays: 60, warnDays: 30 },
  entities: DEFAULT_ENTITY_RULES,
  compliance: { versions: [] },
  digest: { enabled: false, recipients: [] },
};

const intIn = (v: unknown, lo: number, hi: number): number | undefined =>
  typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi ? v : undefined;

/** Merge a stored value over the defaults, dropping anything malformed. */
export function mergeSettings(stored: unknown): PracticeSettings {
  const s = (stored && typeof stored === 'object' ? stored : {}) as Record<string, unknown>;
  const cad = (s.callCadenceMonths && typeof s.callCadenceMonths === 'object' ? s.callCadenceMonths : {}) as Record<string, unknown>;
  const callCadenceMonths = { ...DEFAULT_SETTINGS.callCadenceMonths };
  for (const seg of SEGMENTS) {
    if (cad[seg] === null) callCadenceMonths[seg] = null;
    else {
      const n = intIn(cad[seg], 1, 60);
      if (n !== undefined) callCadenceMonths[seg] = n;
    }
  }
  const fc = (s.feeConsent && typeof s.feeConsent === 'object' ? s.feeConsent : {}) as Record<string, unknown>;
  const en = (s.entities && typeof s.entities === 'object' ? s.entities : {}) as Record<string, unknown>;
  const cp = (s.compliance && typeof s.compliance === 'object' ? s.compliance : {}) as Record<string, unknown>;
  const versions: ComplianceRuleVersion[] = [];
  for (const raw of Array.isArray(cp.versions) ? cp.versions : []) {
    const v = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    const w = (v.windows && typeof v.windows === 'object' ? v.windows : {}) as Record<string, unknown>;
    if (typeof v.effectiveFrom !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(v.effectiveFrom)) continue;
    const windows = Object.fromEntries(RULE_KEYS.map((k) => [k, intIn(w[k], 1, 3650)]));
    if (Object.values(windows).some((x) => x === undefined)) continue;
    versions.push({
      effectiveFrom: v.effectiveFrom,
      windows: windows as ComplianceRuleVersion['windows'],
      ...(typeof v.note === 'string' && v.note.trim() ? { note: v.note.trim().slice(0, 500) } : {}),
      ...(typeof v.changedBy === 'string' ? { changedBy: v.changedBy.slice(0, 200) } : {}),
      ...(typeof v.changedAt === 'string' ? { changedAt: v.changedAt.slice(0, 40) } : {}),
    });
  }
  versions.sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom));
  const dg = (s.digest && typeof s.digest === 'object' ? s.digest : {}) as Record<string, unknown>;
  const recipients = Array.isArray(dg.recipients)
    ? dg.recipients.filter((r): r is string => typeof r === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r)).slice(0, 20)
    : DEFAULT_SETTINGS.digest.recipients;
  return {
    callCadenceMonths,
    feeConsent: {
      renewalWindowDays: intIn(fc.renewalWindowDays, 0, 365) ?? DEFAULT_SETTINGS.feeConsent.renewalWindowDays,
      warnDays: intIn(fc.warnDays, 0, 180) ?? DEFAULT_SETTINGS.feeConsent.warnDays,
    },
    entities: {
      strategyReviewMonths: intIn(en.strategyReviewMonths, 1, 60) ?? DEFAULT_ENTITY_RULES.strategyReviewMonths,
      deedReviewMonths: intIn(en.deedReviewMonths, 1, 120) ?? DEFAULT_ENTITY_RULES.deedReviewMonths,
      auditOverdueMonths: intIn(en.auditOverdueMonths, 1, 36) ?? DEFAULT_ENTITY_RULES.auditOverdueMonths,
      warnDays: intIn(en.warnDays, 0, 180) ?? DEFAULT_ENTITY_RULES.warnDays,
    },
    compliance: { versions: versions.slice(-50) },
    digest: {
      enabled: typeof dg.enabled === 'boolean' ? dg.enabled : DEFAULT_SETTINGS.digest.enabled,
      recipients,
    },
  };
}
